import {
    unionBy,
    map,
    find,
    mergeWith,
    reduce,
    has,
    forOwn,
    keys,
    union,
    orderBy,
    filter,
    chain,
    sortBy,
    pickBy,
    values,
} from "lodash";
import { createSelector } from "reselect";
import { IntlShape } from "react-intl";
import { ChartDataset, ChartData } from "chart.js";
import { Map } from "immutable";
import moment from "moment";

import { AuthNormalisedEntities } from "@auth/models";
import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
    getEntities as getAuthEntities,
} from "@auth/selectors";
import { FilesNormalisedEntities } from "@files/models";
import { getEntities as getFilesEntities } from "@files/selectors";
import { LocalisationNormalisedEntities, Region } from "@localisation/models";
import { getEntities as getLocalisationEntities, makeHydrateRegion } from "@localisation/selectors";
import { MusicNormalisedEntities, Album, Track } from "@music/models";
import { getEntities as getMusicEntities, makeHydrateTrack } from "@music/selectors";
import { makeHasMorePagesParametric, makeGetNextPageParametric } from "@orm/selectors";
import {
    rgbStringDarker,
    rgbStringDarkerByDsp,
    rgbStringPrimary,
    rgbStringPrimaryByDsp,
} from "@util/index";
import {
    SalesState as State,
    TrendsSet,
    SalesSet,
    DataSet,
    DemographicsSet,
    PlaylistTrendsSet,
    HeadlineStatsSet,
    AggregatedSalesSet,
    AggregatedAlbumSalesSet,
    AggregatedTrackSalesSet,
    AggregatedArtistSalesSet,
    SalesData,
    isAlbumSalesSet,
    isTrackSalesSet,
    isArtistSalesSet,
    isAggregatedAlbumSalesSet,
    isAggregatedTrackSalesSet,
    isAggregatedArtistSalesSet,
} from "../models";
import { Playlist, VendorNormalisedEntities } from "@vendor/models";
import { getEntities as getVendorEntities, makeHydratePlaylist } from "@vendor/selectors";
import { messages } from "../messages";

export interface MusicIdProps {
    albumId?: number;
    trackId?: number;
    artistId?: number;
}

interface AlbumsTracksProps {
    albums?: Album[];
    tracks?: Track[];
}

export interface AlbumTrackVendorProps extends MusicIdProps {
    vendor?: string;
}

export interface PeriodProps {
    from: Date;
    to: Date;
}

export interface IntlProps {
    intl: IntlShape;
}

export interface SaleRow {
    entity: Album | Track;
    sales: AggregatedSalesSet;
}

interface DataSetsShown {
    [key: string]: boolean;
}

// Some helper functions
const mergeFunction = (objValue: any, srcValue: any, key: string): any => {
    if (key === "data" && !Array.isArray(srcValue)) {
        // Custom merge for the data rows y date.
        let retval = Map<string, number>(objValue != null ? objValue : {});
        const srcMap = Map<string, number>(srcValue);
        srcMap.map((srcVal, date) => {
            const retValue = retval.has(date) ? srcVal + retval.get(date) : srcVal;
            retval = retval.set(date, retValue);
        });
        return retval.toJS();
    }
    else if (key === "data") {
        // To merge rows, we need to ensure we match them by type.
        const allTypes = unionBy(objValue, srcValue, "type");
        return map(allTypes, (dataSet: SalesData) => mergeWith(
            find(objValue, ["type", dataSet.type]),
            find(srcValue, ["type", dataSet.type]),
            mergeFunction,
        ));
    }
    else if (key === "count") {
        // Ensure counts increment.
        return objValue != null ? objValue + srcValue : srcValue;
    }
    return undefined;
};

const mergeSets = (original: SalesSet, sets: SalesSet[]) =>
    reduce(sets, (result, set) => mergeWith(result, set, mergeFunction), {
        kind: original.kind,
        from: original.from,
        to: original.to,
        groupBy: original.groupBy,
        data: [],
    } as SalesSet);

const filterSalesSetByTypeCountries = (
    set: SalesSet,
    type: "stream" | "download",
    countries: string[],
) => {
    if (set != null && countries.length > 0) {
        const sets = map(countries, (code) => find(set.countries, ["code", code]));
        set = mergeSets(set, sets);
    }
    return set != null ? set.data.filter(
        (row) => row.type.endsWith(type),
    ) : null;
};

const filterSalesDataByDatasets = (data: SalesData[], datasetsShown: DataSetsShown) =>
    data.filter(
        (row) => datasetsShown[row.type] == null || datasetsShown[row.type],
    );

const aggregateSalesSet = (
    set: SalesSet,
    type: "stream" | "download",
    countries: string[],
    datasetsShown: DataSetsShown = null,
) => {
    // Build the aggregated sales
    const aggregated: AggregatedSalesSet = {
        kind: "all",
        total: 0,
        types: {},
    };
    if (isAlbumSalesSet(set)) {
        aggregated.kind = "album";
        (aggregated as AggregatedAlbumSalesSet).id = set.id;

        // And get the aggregated track sales.
        if (set.tracks != null) {
            const aggregatedTracks = map(
                set.tracks,
                (trackSet) => aggregateSalesSet(trackSet, type, countries, datasetsShown) as AggregatedTrackSalesSet,
            );

            (aggregated as AggregatedAlbumSalesSet).tracks = aggregatedTracks;
        }
    }
    if (isTrackSalesSet(set)) {
        aggregated.kind = "track";
        (aggregated as AggregatedTrackSalesSet).id = set.id;

        // And get the aggregated album sales.
        if (set.albums != null) {
            const aggregatedAlbums = map(
                set.albums,
                (albumSet) => aggregateSalesSet(albumSet, type, countries, datasetsShown) as AggregatedAlbumSalesSet,
            );

            (aggregated as AggregatedTrackSalesSet).albums = aggregatedAlbums;
        }
    }
    if (isArtistSalesSet(set)) {
        aggregated.kind = "artist";
        (aggregated as AggregatedArtistSalesSet).id = set.id;

        // And get the aggregated album sales.
        if (set.albums != null) {
            const aggregatedAlbums = map(
                set.albums,
                (albumSet) => aggregateSalesSet(albumSet, type, countries, datasetsShown) as AggregatedAlbumSalesSet,
            );

            (aggregated as AggregatedArtistSalesSet).albums = aggregatedAlbums;
        }
    }

    // Now set the totals from the data sets.
    let dataSets = filterSalesSetByTypeCountries(set, type, countries);
    if (datasetsShown != null) {
        dataSets = filterSalesDataByDatasets(dataSets, datasetsShown);
    }
    aggregated.types = reduce(dataSets, (types, dataSet) => {
        let total = 0;
        if (dataSet.count != null) {
            total = dataSet.count;
        }
        else {
            forOwn(dataSet.data, (d) => total += d);
        }
        types[dataSet.type] = total;
        aggregated.total += total;
        return types;
    }, aggregated.types);

    return aggregated;
};

// The selectors
export const getSalesSet = ({ sales }: State) => sales.data.sales.all;
export const getAlbumSales = ({ sales }: State) => sales.data.sales.albums;
export const getTrackSales = ({ sales }: State) => sales.data.sales.tracks;
export const getArtistSales = ({ sales }: State) => sales.data.sales.artists;
export const getDemographicsSet = ({ sales }: State) => sales.data.demographics.all;
export const getAlbumDemographics = ({ sales }: State) => sales.data.demographics.albums;
export const getTrackDemographics = ({ sales }: State) => sales.data.demographics.tracks;
export const getArtistDemographics = ({ sales }: State) => sales.data.demographics.artists;
export const getPlaylistTrendsSet = ({ sales }: State) => sales.data["playlist-trends"].all;
export const getAlbumPlaylistTrends = ({ sales }: State) => sales.data["playlist-trends"].albums;
export const getTrackPlaylistTrends = ({ sales }: State) => sales.data["playlist-trends"].tracks;
export const getArtistPlaylistTrends = ({ sales }: State) => sales.data["playlist-trends"].artists;
export const getHeadlineStatsSet = ({ sales }: State) => sales.data["headline-stats"].all;
export const getAlbumHeadlineStats = ({ sales }: State) => sales.data["headline-stats"].albums;
export const getTrackHeadlineStats = ({ sales }: State) => sales.data["headline-stats"].tracks;
export const getArtistHeadlineStats = ({ sales }: State) => sales.data["headline-stats"].artists;
export const getHeadlineStatsMap = ({ sales }: State) => sales.headlineStats;
const getProps = (_state: State, props: AlbumTrackVendorProps | IntlProps | AlbumsTracksProps | PeriodProps) => props;

// Settings
export const getSettingFrom = ({ sales }: State) => sales.settings.from;
export const getSettingTo = ({ sales }: State) => sales.settings.to;
export const getSettingType = ({ sales }: State) => sales.settings.type;
export const getSettingCountries = ({ sales }: State) => sales.settings.countries;
export const getSettingDatasetsShown = ({ sales }: State) => sales.settings.datasetsShown;

const makeGetSetForProps = <S extends TrendsSet>() => (
    set: S,
    albums: Map<number, S>,
    tracks: Map<number, S>,
    artists: Map<number, S>,
    props: MusicIdProps,
) => {
    if (props.albumId == null && props.trackId == null && props.artistId == null &&
        set != null && set.lastLoaded != null
    ) {
        return set;
    }
    else if (props.albumId != null && albums.has(props.albumId)) {
        return albums.get(props.albumId);
    }
    else if (props.trackId != null && tracks.has(props.trackId)) {
        return tracks.get(props.trackId);
    }
    else if (props.artistId != null && artists.has(props.artistId)) {
        return artists.get(props.artistId);
    }
    return null;
};

export const getSalesSetForProps = createSelector(
    getSalesSet,
    getAlbumSales,
    getTrackSales,
    getArtistSales,
    getProps,
    makeGetSetForProps<SalesSet>(),
);

export const getDemographicsSetForProps = createSelector(
    getDemographicsSet,
    getAlbumDemographics,
    getTrackDemographics,
    getArtistDemographics,
    getProps,
    makeGetSetForProps<DemographicsSet>(),
);

export const getPlaylistTrendsSetForProps = createSelector(
    getPlaylistTrendsSet,
    getAlbumPlaylistTrends,
    getTrackPlaylistTrends,
    getArtistPlaylistTrends,
    getProps,
    makeGetSetForProps<PlaylistTrendsSet>(),
);

export const getHeadlineStatsSetForProps = createSelector(
    getHeadlineStatsSet,
    getAlbumHeadlineStats,
    getTrackHeadlineStats,
    getArtistHeadlineStats,
    getProps,
    makeGetSetForProps<HeadlineStatsSet>(),
);

interface DataSetPair {
    label: string;
    total: number;
}
interface DemographicsSelectorOptions {
    sortKey: string;
    sortOrder?: "asc" | "desc";
    filterFn?: (arr: DataSet[], props: AlbumTrackVendorProps) => DataSet[];
}
const makeGetDemographicsForProps = (setKey: "gender" | "age" | "region", opts: DemographicsSelectorOptions) =>
    createSelector(
        getDemographicsSetForProps,
        getProps,
        (set: DemographicsSet, props: AlbumTrackVendorProps) => {
            const { sortKey, sortOrder, filterFn } = {
                sortOrder: "asc",
                vendor: null,
                filterFn: (arr: DataSet[]) => arr,
                ...opts,
            } as DemographicsSelectorOptions;
            return set == null || set.data == null || set.data[setKey] == null
            ? []
            : orderBy(reduce(filterFn(set.data[setKey], props), (demoMap, item) => {
                forOwn(item.data, (num, key) => {
                    let row: DataSetPair = {
                        label: key,
                        total: 0,
                    };
                    if (demoMap.has(key)) {
                        row = demoMap.get(key);
                    }
                    row.total += num;
                    demoMap = demoMap.set(key, row);
                });
                return demoMap;
            }, Map<string, DataSetPair>()).toArray(), sortKey, sortOrder);
        },
    );
const getAgeDataForProps = makeGetDemographicsForProps("age", {
    sortKey: "label",
    filterFn: (arr, { vendor }) => filter(arr, { vendor }),
});
const getGenderDataForProps = makeGetDemographicsForProps("gender", {sortKey: "total", sortOrder: "desc"});
const getRegionDataForProps = makeGetDemographicsForProps("region", {sortKey: "total", sortOrder: "desc"});

export const getSalesGroupBy = createSelector(
    getSalesSetForProps,
    (set: SalesSet) => set != null ? set.groupBy : null,
);

export const haveSalesData = createSelector(
    getSalesSetForProps,
    (set: SalesSet) => set != null,
);

export const haveDemographicsData = createSelector(
    getDemographicsSetForProps,
    (set: DemographicsSet) => set != null,
);

export const haveHeadlineStatsData = createSelector(
    getHeadlineStatsSetForProps,
    (set: HeadlineStatsSet) => set != null,
);

// Function to get when the data was last loaded.
export const getSalesLastLoadedForProps = createSelector(
    getSalesSetForProps,
    (set: SalesSet) => set != null ? set.lastLoaded : null,
);
export const getDemographicsLastLoadedForProps = createSelector(
    getDemographicsSetForProps,
    (set: DemographicsSet) => set != null ? set.lastLoaded : null,
);
export const getPlaylistTrendsLastLoadedForProps = createSelector(
    getPlaylistTrendsSetForProps,
    (set: PlaylistTrendsSet) => set != null ? set.lastLoaded : null,
);
export const getHeadlineStatsLastLoadedForProps = createSelector(
    getHeadlineStatsSetForProps,
    (set: HeadlineStatsSet) => set != null ? set.lastLoaded : null,
);

export const getSalesDataForSettings = createSelector(
    getSalesSetForProps,
    getSettingType,
    getSettingCountries,
    filterSalesSetByTypeCountries,
);

export const getSalesTypes = createSelector(
    getSalesDataForSettings,
    (dataSets: SalesData[]) => dataSets == null ? null :
        map(dataSets, (set: SalesData) => set.type),
);

export const getTotalCount = createSelector(
    getSalesDataForSettings,
    (data: SalesData[]) => {
        if (data != null) {
            return reduce(data, (result: number, dataSet: SalesData) => {
                forOwn(dataSet.data, (d) => result += d);
                return result;
            }, 0);
        }
        return null;
    },
);
export const getTotalCountForType = createSelector(
    getSalesDataForSettings,
    getSettingDatasetsShown,
    (data: SalesData[], datasetsShown: DataSetsShown) => {
        if (data != null) {
            return reduce(data, (result: number, dataSet: SalesData) => {
                if (datasetsShown[dataSet.type] == null || datasetsShown[dataSet.type] === true) {
                    forOwn(dataSet.data, (d) => result += d);
                }
                return result;
            }, 0);
        }
        return null;
    },
);

const getStatForPeriod = (stat: string) =>
    ({ from, to }: PeriodProps, headlineStats: Map<string, HeadlineStatsSet>) => {
        const key = moment(from).format("YYYYMMDD") + "_" + moment(to).format("YYYYMMDD");
        if (headlineStats.has(key)) {
            const set = headlineStats.get(key);
            const idx = set.headings.indexOf(stat);
            if (idx > -1) {
                return set.data.all[idx];
            }
        }
        return null;
    };

export const getTotalStreamsForPeriod = createSelector(
    getProps,
    getHeadlineStatsMap,
    getStatForPeriod("total"),
);

export const getPlaylistStreamsForPeriod = createSelector(
    getProps,
    getHeadlineStatsMap,
    getStatForPeriod("playlists"),
);

export const getNewReleasesStreamsForPeriod = createSelector(
    getProps,
    getHeadlineStatsMap,
    getStatForPeriod("new_releases"),
);

export const getSalesDataDates = createSelector(
    getSalesDataForSettings,
    (dataSets: SalesData[]) =>
        dataSets != null ?
            reduce(dataSets, (result: string[], dataSet: SalesData) => {
                const dates = keys(dataSet.data);
                result = union(result, dates);
                result.sort();
                return result;
            }, []) : null,
);

export const getAggregateSales = createSelector(
    getSalesSetForProps,
    getSettingType,
    getSettingCountries,
    (set: SalesSet, type: "stream" | "download", countries: string[]) =>
        set == null ? null : aggregateSalesSet(set, type, countries),
);

type AlbumTrackAggregatedSales = AggregatedAlbumSalesSet | AggregatedTrackSalesSet |
    AggregatedArtistSalesSet;

const mapAggregateSalesToTable =
    (entities: Album[] | Track[], aggSales: AlbumTrackAggregatedSales[], types: string[]) =>
        map(entities, (entity: Album | Track) => {
            let sales = find(aggSales, ["id", entity.id]);
            if (sales == null) {
                sales = {
                    kind: null,
                    id: entity.id,
                    total: 0,
                    types: reduce(types,
                        (result: {[key: string]: number}, type: string) => {
                            result[type] = 0;
                            return result;
                        }, {},
                    ),
                };
                if (entity instanceof Album) {
                    (sales as AggregatedAlbumSalesSet).kind = "album";
                }
                else {
                    (sales as AggregatedTrackSalesSet).kind = "track";
                }
            }
            // Fill in any missing data.
            for (const type of types) {
                if (sales.types[type] == null) {
                    sales.types[type] = 0;
                }
            }
            return {
                entity,
                sales,
            } as SaleRow;
        });

export const getAggregateSalesForTable = createSelector(
    getProps,
    getAggregateSales,
    getSalesTypes,
    (props: MusicIdProps & AlbumsTracksProps, aggregateSales: AggregatedSalesSet, types: string[]) => {
        if (props.tracks != null) {
            return mapAggregateSalesToTable(
                props.tracks,
                aggregateSales != null && isAggregatedAlbumSalesSet(aggregateSales) ? aggregateSales.tracks : [],
                types != null ? types : [],
            );
        }
        else if (props.albums != null) {
            return mapAggregateSalesToTable(
                props.albums,
                aggregateSales != null &&
                    (isAggregatedTrackSalesSet(aggregateSales) || isAggregatedArtistSalesSet(aggregateSales))
                        ? aggregateSales.albums
                        : [],
                types != null ? types : [],
            );
        }
        return [];
    },
);

export const getSalesByCountry = createSelector(
    getSalesSetForProps,
    getSettingType,
    getProps,
    getSettingDatasetsShown,
    (
        set: SalesSet,
        type: "stream" | "download",
        props: IntlProps,
        datasetsShown: DataSetsShown,
    ) => {
        const { intl: { formatMessage, formatNumber } } = props;
        const headings = [formatMessage(messages.labelCountry), formatMessage(messages.salesTotal), {
            role: "tooltip",
            type: "string",
            p: {html: true},
        }];
        let data: any[][] = [];
        if (set != null && set.countries != null) {
            data = map(set.countries, (countrySet) => {
                const aggregate = aggregateSalesSet(countrySet, type, [], datasetsShown);
                const row = new Array(3);
                row[0] = countrySet.code;
                row[1] = aggregate.total;
                row[2] = "";
                const typesMap = Map(aggregate.types);
                typesMap.map((_typeVal, typeKey) => {
                    const label = formatMessage({
                        id: "sales.graph." + typeKey,
                        defaultMessage: typeKey,
                    });
                    row[2] += label + ": <strong>" + formatNumber(aggregate.types[typeKey]) + "</strong><br>";
                });
                row[2] += headings[1] + ": <strong>" + formatNumber(aggregate.total) + "</strong>";
                return row;
            });
        }
        data.unshift(headings);
        return data;
    },
);

export const getSalesDataSets = createSelector(
    haveSalesData,
    getSalesDataForSettings,
    getSalesDataDates,
    getProps,
    getSettingDatasetsShown,
    (
        hasSalesData: boolean,
        dataSets: SalesData[],
        months: string[],
        props: IntlProps,
        datasetsShown: DataSetsShown,
    ) =>
        hasSalesData && dataSets != null
            ? map(dataSets, (dataSet: SalesData, index: number) => {
                // Fill the data array with the value from the dataset or 0.
                const datasetData: number[] = reduce(months,
                    (data: number[], month: string) => {
                        const idx = months.indexOf(month);
                        data[idx] = 0;
                        if (has(dataSet.data, month)) {
                            data[idx] = dataSet.data[month];
                        }
                        return data;
                    }, []);

                // Get the colours.
                const primaryColour = rgbStringPrimaryByDsp(dataSet.type);
                const darkerColour = rgbStringDarkerByDsp(dataSet.type);
                const isHidden = datasetsShown.hasOwnProperty(dataSet.type)
                    ? !datasetsShown[dataSet.type]
                    : false;

                return {
                    label: props.intl.formatMessage({
                        id: "sales.graph." + dataSet.type,
                        defaultMessage: dataSet.type,
                    }),
                    fill: false,
                    lineTension: 0.25,
                    backgroundColor: primaryColour,
                    borderColor: primaryColour,
                    borderCapStyle: "butt",
                    borderDash: [],
                    borderDashOffset: 0.0,
                    borderJoinStyle: "round",
                    // pointBorderColor: primaryColour,
                    pointBackgroundColor: primaryColour,
                    pointBorderWidth: 1,
                    pointHoverRadius: 5,
                    pointHoverBackgroundColor: primaryColour,
                    pointHoverBorderColor: darkerColour,
                    pointHoverBorderWidth: 2,
                    pointRadius: 1,
                    pointHitRadius: 10,
                    data: datasetData,
                    hidden: isHidden,
                } as ChartDataset;
            }) : [],
);

export const makeGetGenderChartsData = () => createSelector(
    haveDemographicsData,
    getGenderDataForProps,
    getProps,
    (
        hasDemographicsData: boolean,
        genderData: DataSetPair[],
        { intl: { formatMessage } }: IntlProps,
    ) => {
        // Ensure in m/f/u order, neutral after
        const labels = ["male", "female", "unknown"].reverse();
        const rawData = sortBy(genderData, (row) => labels.indexOf(row.label)).reverse();
        const data: ChartData<"pie"> = hasDemographicsData && genderData.length > 0
            ? {
                labels: map(rawData, (row) => formatMessage({
                    id: "sales.gender." + row.label,
                    defaultMessage: row.label,
                })),
                datasets: [{
                    data: map(rawData, (row) => row.total),
                    backgroundColor: map(rawData, (_row, key) => rgbStringPrimary(key)),
                    hoverBackgroundColor: map(rawData, (_row, key) => rgbStringDarker(key)),
                    borderWidth: 2,
                }],
            }
            : {labels: [], datasets: []};
        return data;
    },
);

export const makeGetAgeChartsData = () => createSelector(
    haveDemographicsData,
    getAgeDataForProps,
    getProps,
    (
        hasDemographicsData: boolean,
        ageData: DataSetPair[],
        { intl: { formatMessage } }: IntlProps,
    ) => {
        const data: ChartData<"bar"> = hasDemographicsData && ageData.length > 0
            ? {
                labels: map(ageData, (row) => row.label.match(/^\d/)
                    ? row.label
                    : formatMessage({
                        id: "sales.age-group." + row.label,
                        defaultMessage: row.label,
                    })),
                datasets: [{
                    data: map(ageData, (row) => row.total),
                    backgroundColor: rgbStringPrimary(8),
                    hoverBackgroundColor: rgbStringDarker(8),
                }],
            }
            : {labels: [], datasets: []};
        return data;
    },
);

export interface RegionSetPair {
    total: number;
    rank: number;
    entity: Region;
}
export const makeGetRegionDataHydrated = () => createSelector(
    getRegionDataForProps,
    getLocalisationEntities,
    (data: DataSetPair[], lEntities: LocalisationNormalisedEntities) => {
        const hydrateRegion = makeHydrateRegion(lEntities);
        return map(data, (pair, key) => ({
            total: pair.total,
            rank: key + 1,
            entity: hydrateRegion(pair.label),
        } as RegionSetPair));
    },
);

// Playlist trends pagination selectors
export interface TrackPair {
    total: number;
    percentage: number;
    entity: Track;
}
export interface TrackSet {
    count: number;
    items: TrackPair[];
}
export interface PlaylistSetPair {
    total: number;
    trackSet: TrackSet;
    rank: number;
    entity: Playlist;
}
export const makeGetPlaylistTrends = () => createSelector(
    getPlaylistTrendsSetForProps,
    getVendorEntities,
    getMusicEntities,
    getFilesEntities,
    getAuthEntities,
    (
        set: PlaylistTrendsSet,
        entities: VendorNormalisedEntities,
        music: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydratePlaylist = makeHydratePlaylist(entities);
        const hydrateTrack = makeHydrateTrack(music, files, auth);
        return set == null || set.data == null
            ? []
            : map(set.data, (row, key) => ({
                total: row.total,
                trackSet: {
                    count: row.trackCount,
                    items: chain(row.trackIds)
                        .filter((tr) => tr.id != null)
                        .map((tr) => {
                            let track = hydrateTrack(tr.id);
                            if (track == null) {
                                track = new Track({id: tr.id});
                            }
                            return {
                                total: tr.total,
                                percentage: (tr.total / row.total),
                                entity: track,
                            } as TrackPair;
                        })
                        .value(),
                },
                rank: key + 1,
                entity: hydratePlaylist(row.playlistId),
            } as PlaylistSetPair));
    },
);

const getPlaylistTrendsPaginationForProps = createSelector(
    getPlaylistTrendsSetForProps,
    (set: PlaylistTrendsSet) => set == null || set.data == null ? null : set.pagination,
);
export const playlistTrendsHasMorePages = makeHasMorePagesParametric(getPlaylistTrendsPaginationForProps);
export const playlistTrendsGetNextPage = makeGetNextPageParametric(getPlaylistTrendsPaginationForProps);

// export const makeGetPropsForRegionGeoChart = () => {
//     const getRegionDataHydrated = makeGetRegionDataHydrated();
//     return createSelector(
//         getRegionDataHydrated,
//         getProps,
//         (data: RegionSetPair[], props: IntlProps) => {
//             const rankedCountries = _(data)
//                 .groupBy("entity.countryCode")
//                 .map((row, countryCode) => ({
//                     label: countryCode,
//                     total: sumBy(row, "total"),
//                 }))
//                 .orderBy("total", "desc")
//                 .value();
//             return filter(data, (row) => row.entity.countryCode === "US").
//                 reduce(
//                     (arr, row) => arr.concat([[row.entity.code, row.total]]),
//                     [["Region", "Total"]] as Array<Array<string | number>>,
//                 );
//         },
//     );
// };

interface HeadlineStats { [name: string]: number; }
const mapHeadlineStatsToHeadings = (headings: string[], stats: number[]) => reduce(
    headings,
    (obj: HeadlineStats, heading, i) => {
        obj[heading] = stats[i] != null ? stats[i] : 0;
        return obj;
    },
    {},
);

const filterHeadlineStatsSetByTypeCountries = (
    set: HeadlineStatsSet,
    type: "stream" | "download",
    countries: string[],
    datasetsShown: DataSetsShown,
): HeadlineStats => {
    if (type === "download" || set == null) {
        return null;
    }
    const { headings, data } = set;

    let sets: number[][] = [];

    const countryMatcher = (_: any, countryCode: string) => countries.indexOf(countryCode) > -1;
    const vendorMatcher = (_: any, vendor: string) => {
        const key = (vendor === "apple" ? "am" : vendor) + "-stream";
        return datasetsShown[key] == null || datasetsShown[key];
    };

    const filteredDatasets = reduce(datasetsShown, (filtered, shown) => filtered || !shown, false);
    if (countries.length === 0 && !filteredDatasets) {
        sets.push(data.all);
    }
    else if (countries.length === 0) {
        sets = values(pickBy(set.data.vendors, vendorMatcher));
    }
    else if (countries.length > 0 && !filteredDatasets) {
        sets = values(pickBy(set.data.countries, countryMatcher));
    }
    else {
        values(pickBy(set.data.countriesVendors, countryMatcher)).forEach(
            (vendorSet) => { sets = sets.concat(values(pickBy(vendorSet, vendorMatcher))); },
        );
    }

    return mapHeadlineStatsToHeadings(
        headings,
        reduce(sets, (carry, aSet) => map(aSet, (val, key) => val + (carry[key] != null ? carry[key] : 0)), []),
    );
};

export const getHeadlineStats = createSelector(
    getHeadlineStatsSetForProps,
    getSettingType,
    getSettingCountries,
    getSettingDatasetsShown,
    filterHeadlineStatsSetByTypeCountries,
);

const hasHeadlineStat = (heading: string) => createSelector(
    getSettingType,
    getHeadlineStatsSetForProps,
    (type: string, set: HeadlineStatsSet) => type === "stream" && set != null && set.headings.indexOf(heading) > -1,
);

const getHeadlineStat = (heading: string) => createSelector(
    getHeadlineStats,
    hasHeadlineStat(heading),
    (stats: HeadlineStats, hasStat: boolean) => hasStat ? stats[heading] : undefined,
);

const getPercentageHeadlineStat = (topHeading: string, bottomHeading: string = "total") => createSelector(
    getHeadlineStats,
    hasHeadlineStat(topHeading),
    hasHeadlineStat(bottomHeading),
    (stats: HeadlineStats, hasTop: boolean, hasBottom: boolean) =>
        hasTop && hasBottom
            ? (stats[bottomHeading] === 0 ? 0 : stats[topHeading] / stats[bottomHeading])
            : undefined,
);

export const getNewReleasesStat = getHeadlineStat("new_releases");
export const getPlaylistsStat = getHeadlineStat("playlists");
export const getTrackedPlaylistsStat = getHeadlineStat("tracked_playlists");
export const getTotalStat = getHeadlineStat("total");
export const getTotalCatalogueStat = getHeadlineStat("total_catalogue");

export const getPercentageNewReleasesStat = getPercentageHeadlineStat("new_releases");
export const getPercentagePlaylistsStat = getPercentageHeadlineStat("playlists");
export const getPercentageTrackedPlaylistsStat = getPercentageHeadlineStat("tracked_playlists");
export const getPercentageOfAllStat = getPercentageHeadlineStat("total", "total_catalogue");

const salesPermissionPrefix = "wata.reporting.sales";
export const canViewSales = canPermissionSelectorFactory(salesPermissionPrefix, "view");
export const getViewSalesLevel = levelPermissionSelectorFactory(salesPermissionPrefix, "view");

const demographicsPermissionPrefix = "wata.reporting.demographics";
export const canViewDemographics = canPermissionSelectorFactory(demographicsPermissionPrefix, "view");
export const getViewDemographicsLevel = levelPermissionSelectorFactory(demographicsPermissionPrefix, "view");

const globalStatsPermissionPrefix = "wata.reporting.global";
export const canViewGlobalStats = canPermissionSelectorFactory(globalStatsPermissionPrefix, "view");
export const getViewGlobalStatsLevel = levelPermissionSelectorFactory(globalStatsPermissionPrefix, "view");

export const canViewStreamDemographics = createSelector(
    getSettingType,
    canViewDemographics,
    (type: "stream" | "download", canView: boolean) => type === "stream" && canView,
);

export const canViewPlaylistTrends = createSelector(
    getSettingType,
    canViewSales,
    (type: "stream" | "download", canView: boolean) => type === "stream" && canView,
);
